import { useState, useEffect, useMemo } from 'react';
import { useGLTF } from '@react-three/drei';
import { modelsConfig, ThreeDModels } from '@/lib/threejs/config';
import * as THREE from 'three';
import { Box, Cylinder, Sphere } from '@react-three/drei';

interface ModelLoaderProps {
  modelType: ThreeDModels;
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
}

export function ModelLoader({ modelType, position = [0, 0, 0], rotation = [0, 0, 0], scale = 1 }: ModelLoaderProps) {
  const [loadError, setLoadError] = useState<boolean>(false);

  // Get model URL based on type
  const modelConfig = useMemo(() => {
    switch (modelType) {
      case ThreeDModels.EV_CHARGER:
        return {
          url: modelsConfig.evcharger.url,
          scale: new THREE.Vector3(scale, scale, scale),
        };
      case ThreeDModels.GREENHOUSE:
        return {
          url: modelsConfig.greenhouse.url,
          scale: new THREE.Vector3(scale * 0.5, scale * 0.5, scale * 0.5),
        };
      case ThreeDModels.RED_HOUSE:
        return {
          url: modelsConfig.redHouse.url,
          scale: new THREE.Vector3(scale * 0.03, scale * 0.03, scale * 0.03),
        };
      case ThreeDModels.BEDROOM:
        return {
          url: modelsConfig.bedroom.url,
          scale: new THREE.Vector3(scale, scale, scale),
        };
      case ThreeDModels.LIGHT:
        return {
          url: modelsConfig.light.url,
          scale: new THREE.Vector3(scale, scale, scale),
        };
      case ThreeDModels.MOTION:
        return {
          url: modelsConfig.motion.url,
          scale: new THREE.Vector3(scale, scale, scale),
        };
      case ThreeDModels.PRESSURE:
        return {
          url: modelsConfig.pressure.url,
          scale: new THREE.Vector3(scale, scale, scale),
        };
      case ThreeDModels.TEMPERATURE:
        return {
          url: modelsConfig.temperature.url,
          scale: new THREE.Vector3(scale, scale, scale),
        };
      case ThreeDModels.VOLTAGE:
        return {
          url: modelsConfig.voltage.url,
          scale: new THREE.Vector3(scale, scale, scale),
        };
      case ThreeDModels.POWER:
        return {
          url: modelsConfig.power.url,
          scale: new THREE.Vector3(scale, scale, scale),
        };
      case ThreeDModels.CURRENT:
        return {
          url: modelsConfig.current.url,
          scale: new THREE.Vector3(scale, scale, scale),
        };
      case ThreeDModels.ENERGY:
        return {
          url: modelsConfig.energy.url,
          scale: new THREE.Vector3(scale, scale, scale),
        };
      default:
        return {
          url: '',
          scale: new THREE.Vector3(scale, scale, scale),
        };
    }
  }, [modelType, scale]);

  // Skip loading if we're having issues with the URL
  const { scene, error } = useGLTF(modelConfig.url || '/fallback-model.glb', true, true);

  // Track loading errors
  useEffect(() => {
    if (error) {
      console.error('Error loading model:', error, 'for model type:', modelType);
      setLoadError(true);
    } else if (!modelConfig.url) {
      setLoadError(true);
    } else {
      setLoadError(false);
    }
  }, [error, modelConfig.url, modelType]);

  // If model loading failed or URL is empty, render fallback
  if (loadError || !modelConfig.url) {
    return renderFallback(modelType, position, rotation, scale);
  }

  try {
    // Model successfully loaded, render it
    return <primitive object={scene.clone()} position={position} rotation={rotation} scale={modelConfig.scale} />;
  } catch (err) {
    console.error('Error rendering model:', err);
    return renderFallback(modelType, position, rotation, scale);
  }
}

// Render a fallback shape if model fails to load
function renderFallback(
  modelType: ThreeDModels,
  position: [number, number, number],
  rotation: [number, number, number],
  scale: number,
) {
  if (modelType === ThreeDModels.EV_CHARGER) {
    // Simple EV charger shape
    return (
      <group position={position} rotation={rotation} scale={scale}>
        <Box args={[2, 5, 2]} position={[0, 2.5, 0]}>
          <meshStandardMaterial color="#444444" />
        </Box>
        <Box args={[2.5, 0.5, 2.5]} position={[0, 0.25, 0]}>
          <meshStandardMaterial color="#222222" />
        </Box>
        <Cylinder args={[0.5, 0.5, 1, 16]} position={[1.5, 3, 0]} rotation={[0, 0, Math.PI / 2]}>
          <meshStandardMaterial color="#222222" />
        </Cylinder>
        <Box args={[0.8, 1, 0.8]} position={[0, 3.8, 0]}>
          <meshStandardMaterial color="#666666" />
        </Box>
        {/* Display panel */}
        <Box args={[1.5, 1, 0.2]} position={[0, 3, -1]}>
          <meshStandardMaterial color="#222222" />
        </Box>
        {/* Charging cable */}
        <Cylinder args={[0.1, 0.1, 3, 8]} position={[2, 1.5, 0]} rotation={[0, 0, Math.PI / 2]}>
          <meshStandardMaterial color="#111111" />
        </Cylinder>
      </group>
    );
  } else if (modelType === ThreeDModels.GREENHOUSE) {
    // Simple greenhouse shape
    return (
      <group position={position} rotation={rotation} scale={[scale, scale, scale]}>
        <Box args={[8, 3, 12]} position={[0, 1.5, 0]}>
          <meshStandardMaterial color="#FFFFFF" transparent opacity={0.4} />
        </Box>
        <Box args={[8.5, 0.2, 12.5]} position={[0, 3.1, 0]}>
          <meshStandardMaterial color="#555555" />
        </Box>
        {/* Simple frame structure */}
        {[-4, 0, 4].map((x) => (
          <Box key={`frame-${x}`} args={[0.2, 3, 12]} position={[x, 1.5, 0]}>
            <meshStandardMaterial color="#555555" />
          </Box>
        ))}
        {[-6, -3, 0, 3, 6].map((z) => (
          <Box key={`frame-${z}`} args={[8, 3, 0.2]} position={[0, 1.5, z]}>
            <meshStandardMaterial color="#555555" />
          </Box>
        ))}
      </group>
    );
  } else if (
    modelType === ThreeDModels.VOLTAGE ||
    modelType === ThreeDModels.POWER ||
    modelType === ThreeDModels.CURRENT ||
    modelType === ThreeDModels.ENERGY
  ) {
    // Electrical sensor representation
    const color =
      {
        [ThreeDModels.VOLTAGE]: '#ffaa22',
        [ThreeDModels.POWER]: '#44ff44',
        [ThreeDModels.CURRENT]: '#22aaff',
        [ThreeDModels.ENERGY]: '#ff44aa',
      }[modelType] || '#aaaaaa';

    return (
      <group position={position} rotation={rotation} scale={scale}>
        <Box args={[1, 0.4, 0.6]}>
          <meshStandardMaterial color="#333333" />
        </Box>
        <Cylinder args={[0.2, 0.2, 0.4, 16]} position={[0, 0.4, 0]} rotation={[0, 0, 0]}>
          <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.5} />
        </Cylinder>
        <Box args={[0.8, 0.2, 0.5]} position={[0, 0.2, 0]}>
          <meshStandardMaterial color="#555555" />
        </Box>
      </group>
    );
  } else if (modelType === ThreeDModels.TEMPERATURE) {
    // Temperature sensor
    return (
      <group position={position} rotation={rotation} scale={scale}>
        <Cylinder args={[0.3, 0.2, 0.8, 16]}>
          <meshStandardMaterial color="#ff4444" />
        </Cylinder>
        <Box args={[0.4, 0.1, 0.4]} position={[0, -0.45, 0]}>
          <meshStandardMaterial color="#333333" />
        </Box>
      </group>
    );
  } else if (modelType === ThreeDModels.PRESSURE) {
    // Pressure sensor
    return (
      <group position={position} rotation={rotation} scale={scale}>
        <Cylinder args={[0.5, 0.5, 0.2, 16]}>
          <meshStandardMaterial color="#8844ff" />
        </Cylinder>
        <Cylinder args={[0.3, 0.3, 0.4, 16]} position={[0, 0.3, 0]}>
          <meshStandardMaterial color="#333333" />
        </Cylinder>
        <Box args={[0.8, 0.1, 0.8]} position={[0, -0.15, 0]}>
          <meshStandardMaterial color="#555555" />
        </Box>
      </group>
    );
  } else if (modelType === ThreeDModels.LIGHT) {
    // Light sensor
    return (
      <group position={position} rotation={rotation} scale={scale}>
        <Box args={[0.8, 0.2, 0.8]}>
          <meshStandardMaterial color="#333333" />
        </Box>
        <Box args={[0.6, 0.1, 0.6]} position={[0, 0.15, 0]}>
          <meshStandardMaterial color="#ffaa44" emissive="#ffaa44" emissiveIntensity={0.5} />
        </Box>
      </group>
    );
  } else if (modelType === ThreeDModels.MOTION) {
    // Motion sensor
    return (
      <group position={position} rotation={rotation} scale={scale}>
        <Box args={[0.7, 0.7, 0.2]}>
          <meshStandardMaterial color="#333333" />
        </Box>
        <Sphere args={[0.25, 16, 16]} position={[0, 0, 0.1]}>
          <meshStandardMaterial color="#44ffaa" />
        </Sphere>
      </group>
    );
  }

  // Generic fallback for any other type
  return (
    <Sphere args={[0.7 * scale, 16, 16]} position={position} rotation={rotation}>
      <meshStandardMaterial color="#888888" wireframe />
    </Sphere>
  );
}
